/**
 * Mahjong Solitaire layouts.
 *
 * Layouts are geometry, not renderer behavior. A layout is a set of slots in a
 * half-cell grid: every tile occupies a 2x2 half-cell footprint, so upper
 * layers can be shifted by a half-cell while same-layer tiles still sit edge to
 * edge. The engine deals faces onto these slots and proves a winning line.
 */
import {
  KMAHJONGG_LAYOUT_SOURCES,
  type KMahjonggLayoutSource,
} from './kmahjonggLayoutSources';

export interface Slot {
  /** Half-cell column of the tile's top-left corner. */
  x: number;
  /** Half-cell row of the tile's top-left corner. */
  y: number;
  /** Layer index (0 = table). */
  z: number;
}

export interface LayoutSource {
  collection: 'KMahjongg';
  file: string;
  license: 'GPL-2.0-or-later';
  format: 'kmahjongg-layout-v1.0' | 'kmahjongg-layout-v1.1';
}

export interface Layout {
  id: string;
  /** Plain-English display name. */
  name: string;
  /** A short one-line description for the hub card. */
  blurb: string;
  /** Tile count; always even. */
  tileCount: number;
  /** Half-cell width of the normalized bounding box. */
  cols: number;
  /** Half-cell height of the normalized bounding box. */
  rows: number;
  /** Highest layer index used. */
  maxLayer: number;
  /** Whether the high point is expected to sit on the visual center. */
  centered: boolean;
  /** Source metadata for imported layouts. */
  source: LayoutSource;
  /** The slots, in stable order. */
  slots: readonly Slot[];
}

interface FinalizeOptions {
  centered?: boolean;
  centerTolerance?: number;
  source: LayoutSource;
}

interface ParsedKMahjonggLayout {
  format: LayoutSource['format'];
  slots: Slot[];
}

function finalize(
  id: string,
  name: string,
  blurb: string,
  rawSlots: Slot[],
  options: FinalizeOptions,
): Layout {
  const centered = options.centered ?? false;
  const slots = normalize(rawSlots);
  const bounds = measure(slots);
  const layout: Layout = {
    id,
    name,
    blurb,
    tileCount: slots.length,
    cols: bounds.cols,
    rows: bounds.rows,
    maxLayer: bounds.maxLayer,
    centered,
    source: options.source,
    slots,
  };
  const issues = validateLayout(layout, options.centerTolerance);
  if (issues.length > 0) {
    throw new Error(`Layout "${id}" is invalid:\n${issues.map((issue) => `- ${issue}`).join('\n')}`);
  }
  return layout;
}

function normalize(slots: Slot[]): Slot[] {
  if (slots.length === 0) return [];
  const minX = Math.min(...slots.map((slot) => slot.x));
  const minY = Math.min(...slots.map((slot) => slot.y));
  return slots.map((slot) => ({
    x: slot.x - minX,
    y: slot.y - minY,
    z: slot.z,
  }));
}

function measure(slots: readonly Slot[]): { cols: number; rows: number; maxLayer: number } {
  let cols = 0;
  let rows = 0;
  let maxLayer = 0;
  for (const slot of slots) {
    cols = Math.max(cols, slot.x + 2);
    rows = Math.max(rows, slot.y + 2);
    maxLayer = Math.max(maxLayer, slot.z);
  }
  return { cols, rows, maxLayer };
}

function overlaps(a: Slot, b: Slot): boolean {
  return Math.abs(a.x - b.x) < 2 && Math.abs(a.y - b.y) < 2;
}

function centerOf(slots: readonly Slot[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const slot of slots) {
    x += slot.x + 1;
    y += slot.y + 1;
  }
  return { x: x / slots.length, y: y / slots.length };
}

export function validateLayout(layout: Layout, centerTolerance = 0.75): string[] {
  const issues: string[] = [];
  const { slots } = layout;

  if (slots.length === 0) issues.push('has no slots');
  if (slots.length % 2 !== 0) issues.push(`has an odd tile count (${slots.length})`);

  const seen = new Set<string>();
  for (const slot of slots) {
    if (!Number.isInteger(slot.x) || !Number.isInteger(slot.y) || !Number.isInteger(slot.z)) {
      issues.push(`contains a non-integer slot (${slot.x},${slot.y},${slot.z})`);
    }
    if (slot.x < 0 || slot.y < 0 || slot.z < 0) {
      issues.push(`contains a negative slot (${slot.x},${slot.y},${slot.z})`);
    }
    const key = `${slot.x},${slot.y},${slot.z}`;
    if (seen.has(key)) issues.push(`duplicates slot ${key}`);
    seen.add(key);
  }

  for (let i = 0; i < slots.length; i += 1) {
    const a = slots[i]!;
    for (let j = i + 1; j < slots.length; j += 1) {
      const b = slots[j]!;
      if (a.z === b.z && overlaps(a, b)) {
        issues.push(`overlaps on layer ${a.z}: (${a.x},${a.y}) and (${b.x},${b.y})`);
      }
    }
  }

  for (const slot of slots) {
    if (slot.z === 0) continue;
    const supported = slots.some((below) => below.z === slot.z - 1 && overlaps(slot, below));
    if (!supported) {
      issues.push(`floats without support: (${slot.x},${slot.y},${slot.z})`);
    }
  }

  const measured = measure(slots);
  if (layout.cols !== measured.cols) issues.push(`cols should be ${measured.cols}, got ${layout.cols}`);
  if (layout.rows !== measured.rows) issues.push(`rows should be ${measured.rows}, got ${layout.rows}`);
  if (layout.maxLayer !== measured.maxLayer) {
    issues.push(`maxLayer should be ${measured.maxLayer}, got ${layout.maxLayer}`);
  }

  if (layout.centered && slots.length > 0) {
    const top = slots.filter((slot) => slot.z === layout.maxLayer);
    const topCenter = centerOf(top);
    const boardCenter = { x: layout.cols / 2, y: layout.rows / 2 };
    if (Math.abs(topCenter.x - boardCenter.x) > centerTolerance) {
      issues.push(
        `top layer is horizontally off-center (${topCenter.x.toFixed(2)} vs ${boardCenter.x.toFixed(2)})`,
      );
    }
    if (Math.abs(topCenter.y - boardCenter.y) > centerTolerance) {
      issues.push(
        `top layer is vertically off-center (${topCenter.y.toFixed(2)} vs ${boardCenter.y.toFixed(2)})`,
      );
    }
  }

  return issues;
}

function parseKMahjonggLayout(raw: string): ParsedKMahjonggLayout {
  const lines = raw.trim().split(/\r?\n/);
  const format = lines[0] as ParsedKMahjonggLayout['format'];
  let width = 0;
  let height = 0;
  let depth = 0;
  const gridLines: string[] = [];

  if (format === 'kmahjongg-layout-v1.0') {
    width = 32;
    height = 16;
    depth = 5;
  } else if (format !== 'kmahjongg-layout-v1.1') {
    throw new Error(`Unsupported KMahjongg layout format "${lines[0]}"`);
  }

  for (const line of lines.slice(1)) {
    if (line.startsWith('#')) continue;
    if (format === 'kmahjongg-layout-v1.1' && line.startsWith('w')) {
      width = Number(line.slice(1));
    } else if (format === 'kmahjongg-layout-v1.1' && line.startsWith('h')) {
      height = Number(line.slice(1));
    } else if (format === 'kmahjongg-layout-v1.1' && line.startsWith('d')) {
      depth = Number(line.slice(1));
    } else if (/^[.1234]+$/.test(line)) {
      gridLines.push(line);
    }
  }

  if (width <= 0 || height <= 0 || depth <= 0) {
    throw new Error(`Invalid KMahjongg dimensions ${width}x${height}x${depth}`);
  }
  if (gridLines.length !== height * depth) {
    throw new Error(
      `KMahjongg grid has ${gridLines.length} rows; expected ${height * depth} for ${width}x${height}x${depth}`,
    );
  }

  const slots: Slot[] = [];
  for (let z = 0; z < depth; z += 1) {
    for (let y = 0; y < height; y += 1) {
      const row = gridLines[z * height + y]!;
      if (row.length !== width) {
        throw new Error(`KMahjongg level ${z} row ${y} has width ${row.length}; expected ${width}`);
      }
      for (let x = 0; x < width; x += 1) {
        if (row[x] !== '1') continue;
        slots.push({ x, y, z });
      }
    }
  }

  return { format, slots };
}

function makeBlurb(layout: { id: string; tileCount: number }): string {
  if (layout.id === 'turtle') return 'The published 144-tile Turtle layout.';
  if (layout.tileCount > 144) {
    return `An extended published layout with ${layout.tileCount} tiles.`;
  }
  if (layout.tileCount < 120) {
    return `A shorter published layout with ${layout.tileCount} tiles.`;
  }
  return `A published ${layout.tileCount}-tile layout.`;
}

function importedLayout(source: KMahjonggLayoutSource): Layout {
  const parsed = parseKMahjonggLayout(source.raw);
  const layout = finalize(
    source.id,
    source.name,
    '',
    parsed.slots,
    {
      centered: false,
      source: {
        collection: 'KMahjongg',
        file: source.sourceFile,
        license: 'GPL-2.0-or-later',
        format: parsed.format,
      },
    },
  );
  return { ...layout, blurb: makeBlurb(layout) };
}

export const LAYOUTS: readonly Layout[] = KMAHJONGG_LAYOUT_SOURCES.map(importedLayout);

function requireLayout(id: string): Layout {
  const layout = getLayout(id);
  if (!layout) throw new Error(`Missing required mahjong layout "${id}"`);
  return layout;
}

export const TURTLE = requireLayout('turtle');
export const GARDEN = requireLayout('garden');

export function getLayout(id: string): Layout | undefined {
  return LAYOUTS.find((layout) => layout.id === id);
}
